import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LoadingScreen from "../LoadingScreen";
import axios from "axios";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import CompanySettings from "../CompanySettings";
import { wpService } from "../../services/wpService";

// Design tokens and form primitives, shared with the Branch section so the two
// halves of this screen cannot drift apart.
import {
  BG_ALT,
  BLUE,
  BLUE_DARK,
  BLUE_PALE,
  BORDER,
  Cell,
  DANGER,
  FieldGrid,
  FieldIcon,
  INK,
  INK_MUTED,
  INK_SOFT,
  IconField,
  SUCCESS,
  SURFACE,
  SectionCard,
  Segmented,
  SubSection,
  ToggleRow,
  fieldSx,
  popperSx,
} from "./storeTheme";

// A store's places. On Add Store there is no store id to POST a branch to yet,
// so the first branch is filled in inline and created with the store; once the
// store exists the panel manages every branch it has.
import BranchPanel, {
  BranchFields,
  branchToPayload,
  emptyBranch,
  validateBranch,
} from "./BranchManager";

// Icons
import { ReactComponent as WAIcon } from "../../assets/icons/whatsapp.svg";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PermIdentityRoundedIcon from "@mui/icons-material/PermIdentityRounded";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StoreRoundedIcon from "@mui/icons-material/StoreRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import AddAPhotoRoundedIcon from "@mui/icons-material/AddAPhotoRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

// store_master.common_store is the group id: it equals the store's own id when
// the store is a parent, the parent's id when it is a child, null when the store
// belongs to no group.
const readStoreGroup = (store) => {
  const groupId = store?.common_store ?? null;
  const selfId = store?.store_id ?? null;

  return {
    isParent: groupId !== null && groupId === selfId,
    parentStoreId: groupId !== null && groupId !== selfId ? groupId : null,
  };
};

// A store is exactly one of these. "normal" is the implicit default: both
// is_pawn and is_travel are 0 on the row.
const STORE_TYPES = [
  { value: "normal", label: "Normal Store", hint: "Standard retail store" },
  { value: "travel", label: "Travel ERP", hint: "Runs the travel ERP module" },
  { value: "pawn", label: "Pawn Store", hint: "Pawn broking — needs a PBL ID" },
];

const readStoreType = (store) => {
  if (store?.is_pawn) return "pawn";
  if (store?.is_travel) return "travel";
  return "normal";
};

// The store id is what every other system (print bridge, API scoping, support
// calls) identifies a store by, so it is shown wherever a store is named.
const storeIdLabel = (storeId) =>
  storeId === "" || storeId === null || storeId === undefined
    ? ""
    : `#${storeId}`;

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components outside StoreMaster to prevent remount on re-render
// ─────────────────────────────────────────────────────────────────────────────

// Two equal columns from lg up — one column below that.
const SplitRow = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
      alignItems: "stretch",
      gap: 1.5,
    }}
  >
    {children}
  </Box>
);

const SaveButton = ({ onClick, loading, isEdit, fullWidth, compact }) => (
  <Button
    onClick={onClick}
    disabled={loading}
    fullWidth={fullWidth}
    startIcon={
      loading ? (
        <CircularProgress
          size={15}
          sx={{ color: compact ? BLUE_DARK : "#fff" }}
        />
      ) : (
        <SaveRoundedIcon />
      )
    }
    sx={{
      textTransform: "none",
      fontSize: compact ? "0.8rem" : "0.85rem",
      fontWeight: 700,
      // In the header the button sits on a dark gradient, so it inverts to a
      // solid white chip; elsewhere it is the blue gradient CTA.
      color: compact ? BLUE_DARK : "#fff",
      px: compact ? 2.25 : 3,
      py: compact ? 0.7 : 1,
      borderRadius: 2,
      minWidth: compact ? 150 : { sm: 190 },
      whiteSpace: "nowrap",
      background: compact
        ? "#fff"
        : `linear-gradient(100deg, ${BLUE} 0%, ${BLUE_DARK} 100%)`,
      boxShadow: compact
        ? "0 2px 8px rgba(15,23,42,0.25)"
        : "0 6px 18px rgba(37,99,235,0.28)",
      "&:hover": {
        background: compact
          ? "#EEF2FF"
          : `linear-gradient(100deg, ${BLUE_DARK} 0%, #1E3A8A 100%)`,
        boxShadow: compact
          ? "0 3px 10px rgba(15,23,42,0.3)"
          : "0 8px 22px rgba(37,99,235,0.34)",
      },
      "&.Mui-disabled": {
        color: compact ? BLUE_DARK : "#fff",
        opacity: 0.7,
      },
    }}
  >
    {isEdit ? "Save Changes" : "Create Store"}
  </Button>
);

// WhatsApp Groups Autocomplete
const GroupAutocomplete = ({
  availableGroupOptions,
  selectedGroupOption,
  handleChange,
  labelText,
  errorText,
}) => (
  <Autocomplete
    options={availableGroupOptions}
    getOptionLabel={(option) => option.name || ""}
    value={selectedGroupOption}
    size="small"
    onChange={(_, newValue) => {
      if (!newValue || newValue.id === "__remove__") {
        handleChange("storeWAGrpID", "");
      } else {
        handleChange("storeWAGrpID", newValue.id);
      }
    }}
    isOptionEqualToValue={(option, value) => option.id === value?.id}
    renderOption={(props, option) => {
      const { key, ...rest } = props;
      return (
        <li key={key} {...rest}>
          {option.id === "__remove__" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CloseRoundedIcon sx={{ fontSize: 16, color: DANGER }} />
              <Typography
                sx={{ fontSize: "0.8rem", color: DANGER, fontWeight: 600 }}
              >
                {option.name}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.83rem", color: INK_SOFT }}>
              {option.name}
            </Typography>
          )}
        </li>
      );
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={labelText}
        placeholder="Select group"
        error={Boolean(errorText)}
        helperText={errorText || undefined}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          ...params.InputProps,
          startAdornment: (
            <>
              <FieldIcon>
                <WAIcon width={18} height={18} />
              </FieldIcon>
              {params.InputProps.startAdornment}
            </>
          ),
        }}
        sx={fieldSx}
      />
    )}
    fullWidth
    slotProps={{ popper: { sx: popperSx } }}
    noOptionsText="No available groups"
  />
);

// Store group (parent / child). A store either heads a group or joins one, so
// picking a parent disables "Make Parent" and vice versa.
const ParentStoreControls = ({
  storeDetails,
  handleChange,
  parentStoreOptions,
  parentStoreLoading,
}) => {
  const selectedParent =
    parentStoreOptions.find(
      (store) => store.store_id === storeDetails.parentStoreId,
    ) || null;

  return (
    <FieldGrid>
      <Cell span={{ xs: 12, md: 5 }}>
        <ToggleRow
          title="Make Parent"
          caption={
            storeDetails.parentStoreId !== null
              ? "Unavailable while a parent is selected"
              : "This store heads its own group"
          }
          checked={storeDetails.isParent}
          disabled={storeDetails.parentStoreId !== null}
          onChange={(e) => {
            handleChange("isParent", e.target.checked);
            if (e.target.checked) handleChange("parentStoreId", null);
          }}
        />
      </Cell>

      <Cell span={{ xs: 12, md: 7 }}>
        <Autocomplete
          options={parentStoreOptions}
          loading={parentStoreLoading}
          disabled={storeDetails.isParent}
          size="small"
          // The id is part of the label so the list can be filtered by typing a
          // store id, and so the picked parent still shows its id once closed.
          getOptionLabel={(option) =>
            [option.store_name, storeIdLabel(option.store_id)]
              .filter(Boolean)
              .join(" · ")
          }
          value={selectedParent}
          onChange={(_, newValue) =>
            handleChange("parentStoreId", newValue ? newValue.store_id : null)
          }
          isOptionEqualToValue={(option, value) =>
            option.store_id === value?.store_id
          }
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.83rem", color: INK_SOFT }}>
                      {option.store_name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED }}>
                      {option.child_count > 0
                        ? `${option.child_count} store(s) under it`
                        : "No stores under it yet"}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      flexShrink: 0,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: BLUE_DARK,
                      bgcolor: BLUE_PALE,
                      border: `1px solid #BFDBFE`,
                      borderRadius: 1,
                      px: 0.6,
                      py: 0.1,
                    }}
                  >
                    {storeIdLabel(option.store_id)}
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Parent Store"
              placeholder="Select a parent"
              InputLabelProps={{ shrink: true }}
              helperText={
                storeDetails.isParent
                  ? "Disabled — this store is a parent"
                  : "Join an existing store group"
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <FieldIcon>
                      <AccountTreeRoundedIcon />
                    </FieldIcon>
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {parentStoreLoading && (
                      <CircularProgress size={14} sx={{ color: BLUE, mr: 3 }} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                ...fieldSx,
                "& .MuiFormHelperText-root": {
                  fontSize: "0.66rem",
                  color: INK_MUTED,
                  ml: 0.5,
                },
              }}
            />
          )}
          slotProps={{ popper: { sx: popperSx } }}
          noOptionsText="No parent stores available"
        />
      </Cell>
    </FieldGrid>
  );
};

// Logo picker
const LogoPicker = ({
  storeImage,
  imageLoading,
  fileInputRef,
  handleImagePick,
  handleRemoveImage,
  isMobile,
}) => {
  const size = isMobile ? 92 : 116;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "row" : "column",
        alignItems: "center",
        gap: isMobile ? 1.75 : 1.25,
        width: isMobile ? "100%" : "auto",
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImagePick}
      />

      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <Tooltip
          title={storeImage.preview ? "Change logo" : "Upload store logo"}
          arrow
        >
          <Box
            onClick={() => !imageLoading && fileInputRef.current?.click()}
            sx={{
              width: size,
              height: size,
              borderRadius: 3,
              cursor: imageLoading ? "default" : "pointer",
              backgroundImage: storeImage.preview
                ? `url(${storeImage.preview})`
                : "none",
              backgroundColor: storeImage.preview ? SURFACE : BLUE_PALE,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              border: storeImage.preview
                ? `1px solid ${BORDER}`
                : `1.5px dashed #BFDBFE`,
              boxShadow: storeImage.preview
                ? "0 4px 14px rgba(15,23,42,0.10)"
                : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: imageLoading ? 0.55 : 1,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: BLUE,
                boxShadow: "0 6px 18px rgba(37,99,235,0.16)",
              },
            }}
          >
            {!storeImage.preview && !imageLoading && (
              <AddAPhotoRoundedIcon
                sx={{ fontSize: isMobile ? 26 : 32, color: BLUE }}
              />
            )}
            {imageLoading && (
              <CircularProgress size={24} sx={{ color: BLUE }} />
            )}
          </Box>
        </Tooltip>

        {storeImage.preview && !imageLoading && (
          <Tooltip title="Remove logo" arrow>
            <IconButton
              onClick={handleRemoveImage}
              size="small"
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                width: 24,
                height: 24,
                bgcolor: DANGER,
                border: "2px solid #fff",
                boxShadow: "0 2px 6px rgba(15,23,42,0.25)",
                "&:hover": { bgcolor: "#B91C1C" },
              }}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ textAlign: isMobile ? "left" : "center", minWidth: 0 }}>
        <Typography
          sx={{ fontSize: "0.75rem", fontWeight: 600, color: INK_SOFT }}
        >
          Store Logo
        </Typography>
        <Typography sx={{ fontSize: "0.66rem", color: INK_MUTED, mt: 0.2 }}>
          {storeImage.preview
            ? "Tap the image to replace"
            : "Tap to upload · PNG/JPG · max 5MB"}
        </Typography>
      </Box>
    </Box>
  );
};

// The company's own details. Address, server IP and the withstanding amount are
// deliberately absent — they belong to a branch, not to the company, and are
// entered in the Branches section below.
const StoreFormFields = ({
  storeDetails,
  handleChange,
  availableGroupOptions,
  selectedGroupOption,
  groupsError,
}) => (
  <FieldGrid>
    <Cell span={{ xs: 12, md: 5, lg: 12 }}>
      <IconField
        icon={<StoreRoundedIcon />}
        label="Store Name"
        required
        placeholder="Enter store name"
        value={storeDetails.storeName}
        onChange={(e) => handleChange("storeName", e.target.value)}
      />
    </Cell>

    <Cell span={{ xs: 12, md: 4, lg: 5 }}>
      <IconField
        icon={<CurrencyRupeeRoundedIcon />}
        label="GST Number"
        placeholder="22AAAAA0000A1Z5"
        value={storeDetails.storeGST}
        onChange={(e) => handleChange("storeGST", e.target.value)}
      />
    </Cell>

    <Cell span={{ xs: 12, md: 3, lg: 7 }}>
      <GroupAutocomplete
        availableGroupOptions={availableGroupOptions}
        selectedGroupOption={selectedGroupOption}
        handleChange={handleChange}
        labelText="WA Group"
        errorText={groupsError}
      />
    </Cell>
  </FieldGrid>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
function StoreMaster() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const navigate = useNavigate();
  const location = useLocation();
  const editStoreData = location?.state;
  const { setReloadAppContext, allStores } = useContext(AutoTaskAuthContext);

  const fileInputRef = useRef(null);

  const [storeImage, setStoreImage] = useState({ preview: null, base64: null });
  const [imageLoading, setImageLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [wpGroups, setWpGroups] = useState([]);
  const [wpGroupsError, setWpGroupsError] = useState("");
  const [assignedGroupIDs, setAssignedGroupIDs] = useState([]);

  const [storeDetails, setStoreDetails] = useState({
    storeID: editStoreData?.store_id || "",
    storeName: editStoreData?.store_name || "",
    storeGST: editStoreData?.gst_number || "",
    storeWAGrpID: editStoreData?.whatsapp_group_id || "",
    storeOwnerName: editStoreData?.head_name || "",
    storeOwnerMobile: editStoreData?.head_phone || "",
    storeOwnerMail: editStoreData?.head_email || "",
    storeStatus: editStoreData?.status || "active",
    storeType: readStoreType(editStoreData),
    pblId: editStoreData?.pbl_id || "",
    ...readStoreGroup(editStoreData),
  });

  // Add Store only. A store cannot exist without a place to trade from, so its
  // first branch is filled in on the same screen and created with it; from then
  // on branches are managed one at a time through their own API.
  const [newBranch, setNewBranch] = useState(emptyBranch);
  const [branchErrorField, setBranchErrorField] = useState(null);

  // Edit only, and only for the header chip — the panel owns the actual list.
  const [branchCount, setBranchCount] = useState(
    editStoreData?.branches?.length ?? editStoreData?.branch_count ?? 0,
  );

  const [parentStoreOptions, setParentStoreOptions] = useState([]);
  const [parentStoreLoading, setParentStoreLoading] = useState(false);

  // Fetch All WhatsApp Groups
  const wpGroupsFetcher = async () => {
    setWpGroupsError("");
    try {
      const groups = await wpService.fetchGroups();
      setWpGroups(groups);
    } catch (error) {
      console.error("Failed to fetch WP groups:", error);
      setWpGroupsError(`Couldn't load WhatsApp groups. ${error.message}`);
    }
  };

  useEffect(() => {
    if (editStoreData) {
      // Fetch wp groups
      wpGroupsFetcher();

      setStoreDetails({
        storeID: editStoreData.store_id || "",
        storeName: editStoreData.store_name || "",
        storeGST: editStoreData.gst_number || "",
        storeWAGrpID: editStoreData.whatsapp_group_id || "",
        storeOwnerName: editStoreData.head_name || "",
        storeOwnerMobile: editStoreData.head_phone || "",
        storeOwnerMail: editStoreData.head_email || "",
        storeStatus: editStoreData.status || "active",
        storeType: readStoreType(editStoreData),
        pblId: editStoreData.pbl_id || "",
        ...readStoreGroup(editStoreData),
      });

      const existingImg =
        editStoreData.store_logo || editStoreData.image || null;
      if (existingImg) setStoreImage({ preview: existingImg, base64: null });
    }
  }, [editStoreData]);

  // Only stores that head a group are selectable as a parent, and a store can
  // never be its own parent — so the one being edited is excluded server-side.
  useEffect(() => {
    const parentStoresFetcher = async () => {
      setParentStoreLoading(true);
      try {
        const selfId = editStoreData?.store_id;
        const query =
          selfId === undefined || selfId === null ? "" : `?exclude=${selfId}`;
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/parent-stores${query}`,
        );
        if (!res?.data?.success) throw new Error(res?.data?.statusMessage);
        setParentStoreOptions(res?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch parent stores:", error);
        setParentStoreOptions([]);
      } finally {
        setParentStoreLoading(false);
      }
    };

    parentStoresFetcher();
  }, [editStoreData]);

  const [loading, setLoading] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackBarConfig((p) => ({ ...p, open: false }));
  };

  const handleChange = (field, value) =>
    setStoreDetails((p) => ({ ...p, [field]: value }));

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setSnackBarConfig({
        open: true,
        message: "Please select a valid image file",
        severity: "error",
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setSnackBarConfig({
        open: true,
        message: "Image must be smaller than 5MB",
        severity: "error",
      });
      return;
    }
    setImageLoading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setStoreImage({ preview: reader.result, base64: reader.result });
      setImageLoading(false);
    };
    reader.onerror = () => {
      setSnackBarConfig({
        open: true,
        message: "Failed to read image file",
        severity: "error",
      });
      setImageLoading(false);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setStoreImage({ preview: null, base64: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resolveImagePayload = () =>
    storeImage.base64 === null ? storeImage.preview || "" : storeImage.base64;

  const isPawnStore = storeDetails.storeType === "pawn";

  // A pawn breaking store cannot be saved without its PBL ID. pbl_id can come
  // back from the API as a number, so it is stringified before trimming.
  const pblMissing = isPawnStore && !String(storeDetails.pblId ?? "").trim();

  const validateCreate = () => {
    if (!storeDetails.storeName?.trim()) return "Store name is required";
    if (!storeDetails.storeOwnerName || !storeDetails.storeOwnerMobile)
      return "Store owner details are required";
    if (pblMissing) return "PBL ID is required for a pawn breaking store";
    return null;
  };

  const validateEdit = () => {
    if (!storeDetails.storeID) return "Store ID is required";
    if (!storeDetails.storeName?.trim()) return "Store name is required";
    if (!storeDetails.storeOwnerName || !storeDetails.storeOwnerMobile)
      return "Store owner details are required";
    if (pblMissing) return "PBL ID is required for a pawn breaking store";
    return null;
  };

  const handleCreateOrUpdateStore = async () => {
    const imagePayload = resolveImagePayload();
    const isCreate = editStoreData === null;
    const error = isCreate ? validateCreate() : validateEdit();
    if (error) {
      setSnackBarConfig({ open: true, message: error, severity: "error" });
      return;
    }

    // On Add Store the first branch is part of the same save, so it is
    // validated here rather than letting the API's own "x is required" be the
    // operator's first hint that the Branch section needs filling in.
    if (isCreate) {
      const branchError = validateBranch(newBranch);
      if (branchError) {
        setBranchErrorField(branchError.field);
        setSnackBarConfig({
          open: true,
          message: branchError.message,
          severity: "error",
        });
        return;
      }
      setBranchErrorField(null);
    }

    setLoading(true);
    const payload = {
      name: storeDetails.storeName,
      gst_number: storeDetails.storeGST,
      storeowner: storeDetails.storeOwnerName,
      owner_mobile: storeDetails.storeOwnerMobile,
      owner_email: storeDetails.storeOwnerMail,
      image: imagePayload,
      service_desc: "",
      whatsapp_group_id: storeDetails.storeWAGrpID,
      // The store type is one choice on screen, two boolean columns on the row.
      is_pawn: isPawnStore,
      is_travel: storeDetails.storeType === "travel",
      pbl_id: isPawnStore ? storeDetails.pblId : null,
      // Group: heading one wins, so common_store is only sent when this store is
      // joining someone else's group. Both keys are always sent so the backend
      // can tell "no group" apart from "not mentioned".
      is_parent: storeDetails.isParent,
      common_store: storeDetails.isParent ? null : storeDetails.parentStoreId,
      // Create carries the store's first branch; on edit the Branch section
      // owns every branch, so nothing branch-shaped is sent from here — a
      // partial branch payload would only risk blanking what it omits.
      ...(isCreate
        ? { branch: branchToPayload(newBranch) }
        : { status: storeDetails.storeStatus }),
    };
    const url = isCreate
      ? `${process.env.REACT_APP_API_BASE_URL}/api/auth/stores`
      : `${process.env.REACT_APP_API_BASE_URL}/api/auth/stores/${storeDetails.storeID}`;
    try {
      const res = isCreate
        ? await axios.post(url, payload)
        : await axios.put(url, payload);
      if (!res?.data?.success) throw res?.data;
      setLoading(false);
      setReloadAppContext((p) => !p);
      setDialogConfig({
        open: true,
        message:
          res?.data?.data?.statusMessage ||
          (isCreate
            ? "Store created successfully"
            : "Store updated successfully"),
        type: "success",
      });
    } catch (err) {
      setLoading(false);
      setReloadAppContext((p) => !p);
      setDialogConfig({
        open: true,
        message:
          err?.statusMessage ||
          err?.response?.data?.statusMessage ||
          (isCreate ? "Failed to create store" : "Failed to update store"),
        type: "error",
      });
    }
  };

  const REMOVE_GROUP_OPTION = { id: "__remove__", name: "Remove Group" };

  // The saved group, even when eNotify's list failed to load or no longer
  // contains it. Without this the picker shows blank, which reads as "no group
  // set" while the store still sends to the saved id.
  const savedGroupOption = storeDetails.storeWAGrpID
    ? wpGroups.find((g) => g.id === storeDetails.storeWAGrpID) || {
        id: storeDetails.storeWAGrpID,
        name: `Saved group (${storeDetails.storeWAGrpID})`,
      }
    : null;

  const availableGroupOptions = [
    ...(storeDetails.storeWAGrpID ? [REMOVE_GROUP_OPTION] : []),
    ...(savedGroupOption && !wpGroups.includes(savedGroupOption)
      ? [savedGroupOption]
      : []),
    ...wpGroups.filter(
      (g) =>
        !assignedGroupIDs.includes(g.id) || g.id === storeDetails.storeWAGrpID,
    ),
  ];

  const selectedGroupOption = savedGroupOption;

  const isEdit = editStoreData !== null;

  return (
    <>
      {loading && <LoadingScreen loadingMessage={"Processing Store Data"} />}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          mt: 1,
          mb: 2,
          gap: 1.5,
        }}
      >
        {/* PAGE HEADER */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            px: { xs: 1.75, sm: 2.25 },
            py: 1.5,
            borderRadius: 3,
            color: "#fff",
            background: `linear-gradient(100deg, ${INK} 0%, ${BLUE_DARK} 100%)`,
            boxShadow: "0 6px 20px rgba(15,23,42,0.18)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2.5,
                bgcolor: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.22)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StoreRoundedIcon sx={{ fontSize: 20, color: "#fff" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.2 }}
                >
                  {isEdit ? "Edit Store" : "Add Store"}
                </Typography>
                {/* A new store has no id yet — it is assigned on save. */}
                {isEdit && storeIdLabel(storeDetails.storeID) && (
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      fontFamily: "monospace",
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      border: "1px solid rgba(255,255,255,0.28)",
                      borderRadius: 1,
                      px: 0.7,
                      py: 0.15,
                    }}
                  >
                    {storeIdLabel(storeDetails.storeID)}
                  </Typography>
                )}
              </Box>
              <Typography
                sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}
              >
                {isEdit
                  ? storeDetails.storeName || "Update store profile & settings"
                  : "Create a new store profile"}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, lg: 2 },
              flexWrap: "wrap",
            }}
          >
            {isEdit && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.7)" }}
                >
                  Status
                </Typography>
                <Segmented
                  size="sm"
                  value={
                    storeDetails.storeStatus === "active"
                      ? "active"
                      : "inactive"
                  }
                  onChange={(next) => handleChange("storeStatus", next)}
                  options={[
                    {
                      value: "active",
                      label: "Active",
                      color: SUCCESS,
                      dot: true,
                    },
                    {
                      value: "inactive",
                      label: "Inactive",
                      color: DANGER,
                      dot: true,
                    },
                  ]}
                />
              </Box>
            )}

            {/* Save sits beside the status toggle once the layout goes
                two-column, so the whole form is actionable without scrolling. */}
            <Box sx={{ display: { xs: "none", lg: "flex" } }}>
              <SaveButton
                compact
                onClick={handleCreateOrUpdateStore}
                loading={loading}
                isEdit={isEdit}
              />
            </Box>
          </Box>
        </Box>

        {/* The split IS the model: everything on the left belongs to the
            company, everything on the right belongs to a place it trades from.
            Nothing store-shaped appears on the right and nothing branch-shaped
            on the left. */}
        <SplitRow>
          {/* ── STORE CONFIGURATION ── */}
          <SectionCard
            icon={<StoreRoundedIcon />}
            title="Store Configuration"
            caption="Identity, owner, module and grouping — the same for every branch"
            action={
              storeIdLabel(storeDetails.storeID) ? (
                <Chip
                  label={storeIdLabel(storeDetails.storeID)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: BLUE_DARK,
                    bgcolor: BLUE_PALE,
                    border: `1px solid #BFDBFE`,
                  }}
                />
              ) : null
            }
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <SubSection label="Store Details" hint="identity & tax">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "flex-start",
                    gap: { xs: 2, md: 2.5 },
                  }}
                >
                  <LogoPicker
                    storeImage={storeImage}
                    imageLoading={imageLoading}
                    fileInputRef={fileInputRef}
                    handleImagePick={handleImagePick}
                    handleRemoveImage={handleRemoveImage}
                    isMobile={isMobile}
                  />

                  <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                    <StoreFormFields
                      storeDetails={storeDetails}
                      handleChange={handleChange}
                      availableGroupOptions={availableGroupOptions}
                      selectedGroupOption={selectedGroupOption}
                      groupsError={wpGroupsError}
                    />
                  </Box>
                </Box>
              </SubSection>

              <SubSection label="Owner Details" hint="primary contact" divider>
                {/* Name · Mobile · E-mail share one line from sm up */}
                <FieldGrid>
                  <Cell span={{ xs: 12, sm: 5 }}>
                    <IconField
                      icon={<PermIdentityRoundedIcon />}
                      label="Owner Name"
                      required
                      placeholder="Full name"
                      value={storeDetails.storeOwnerName}
                      onChange={(e) =>
                        handleChange("storeOwnerName", e.target.value)
                      }
                    />
                  </Cell>
                  <Cell span={{ xs: 5, sm: 3 }}>
                    <IconField
                      icon={<PhoneRoundedIcon />}
                      label="Mobile"
                      required
                      placeholder="10-digit"
                      value={storeDetails.storeOwnerMobile}
                      onChange={(e) =>
                        handleChange(
                          "storeOwnerMobile",
                          e.target.value.replace(/\D/g, ""),
                        )
                      }
                      inputProps={{ maxLength: 10, inputMode: "numeric" }}
                    />
                  </Cell>
                  <Cell span={{ xs: 7, sm: 4 }}>
                    <IconField
                      icon={<EmailRoundedIcon />}
                      label="E-mail"
                      placeholder="name@example.com"
                      value={storeDetails.storeOwnerMail}
                      onChange={(e) =>
                        handleChange("storeOwnerMail", e.target.value)
                      }
                      autoComplete="off"
                    />
                  </Cell>
                </FieldGrid>
              </SubSection>

              <SubSection label="Store Type" hint="module setup" divider>
                <FieldGrid>
                  <Cell span={{ xs: 12, md: 6, lg: 7 }}>
                    <IconField
                      select
                      icon={<StorefrontRoundedIcon />}
                      label="Store Type"
                      value={storeDetails.storeType}
                      onChange={(e) => {
                        const next = e.target.value;
                        handleChange("storeType", next);
                        // A non-pawn store has no PBL ID to keep.
                        if (next !== "pawn") handleChange("pblId", "");
                      }}
                      helperText={
                        STORE_TYPES.find(
                          (type) => type.value === storeDetails.storeType,
                        )?.hint || " "
                      }
                    >
                      {STORE_TYPES.map((type) => (
                        <MenuItem
                          key={type.value}
                          value={type.value}
                          sx={{ fontSize: "0.83rem", color: INK_SOFT }}
                        >
                          {type.label}
                        </MenuItem>
                      ))}
                    </IconField>
                  </Cell>
                  {isPawnStore && (
                    <Cell span={{ xs: 12, md: 6, lg: 5 }}>
                      <IconField
                        icon={<BadgeRoundedIcon />}
                        label="PBL ID"
                        required
                        placeholder="Enter PBL ID"
                        value={storeDetails.pblId || ""}
                        onChange={(e) => handleChange("pblId", e.target.value)}
                        error={pblMissing}
                        helperText={pblMissing ? "PBL ID is required" : " "}
                      />
                    </Cell>
                  )}
                </FieldGrid>
              </SubSection>

              <SubSection
                label="Connected Stores"
                hint="parent / child grouping"
                divider
              >
                <ParentStoreControls
                  storeDetails={storeDetails}
                  handleChange={handleChange}
                  parentStoreOptions={parentStoreOptions}
                  parentStoreLoading={parentStoreLoading}
                />
              </SubSection>
            </Box>
          </SectionCard>

          {/* ── BRANCH CONFIGURATION ──────────────────────────────────────
              On Add Store there is no store id to POST a branch to yet, so the
              first branch is filled in here and created with the store. */}
          <SectionCard
            icon={<PlaceRoundedIcon />}
            title="Branch Configuration"
            caption={
              isEdit
                ? "Every place this store trades from — address, amount, server and remote access"
                : "This store's first place. Add more branches once it is created."
            }
            action={
              isEdit && branchCount > 0 ? (
                <Chip
                  label={`${branchCount} ${branchCount === 1 ? "branch" : "branches"}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: BLUE_DARK,
                    bgcolor: BLUE_PALE,
                    border: `1px solid #BFDBFE`,
                  }}
                />
              ) : null
            }
          >
            {isEdit ? (
              <BranchPanel
                storeId={storeDetails.storeID}
                initialBranches={editStoreData?.branches || []}
                onNotify={(message, severity) =>
                  setSnackBarConfig({ open: true, message, severity })
                }
                onChanged={(list) => {
                  if (Array.isArray(list)) setBranchCount(list.length);
                  // The directory, the store switcher and the cached "current
                  // store" all show branch details, so they have to be re-read.
                  setReloadAppContext((p) => !p);
                }}
              />
            ) : (
              <BranchFields
                form={newBranch}
                onChange={(field, value) =>
                  setNewBranch((prev) => ({ ...prev, [field]: value }))
                }
                errorField={branchErrorField}
              />
            )}
          </SectionCard>
        </SplitRow>

        {/* ADVANCED SETTINGS TOGGLE */}
        <Box sx={{ width: "100%" }}>
          <Box
            onClick={() => setShowAdvanced((prev) => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              width: "100%",
              px: { xs: 1.75, sm: 2.25 },
              py: 1.35,
              borderRadius: showAdvanced ? "12px 12px 0 0" : 3,
              bgcolor: showAdvanced ? BLUE_PALE : SURFACE,
              border: `1px solid ${showAdvanced ? "#BFDBFE" : BORDER}`,
              cursor: "pointer",
              transition: "all 0.22s ease",
              "&:hover": {
                bgcolor: showAdvanced ? BLUE_PALE : BG_ALT,
                borderColor: "#BFDBFE",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 2,
                  bgcolor: showAdvanced ? "#DBEAFE" : BG_ALT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TuneRoundedIcon
                  sx={{
                    fontSize: 17,
                    color: showAdvanced ? BLUE_DARK : INK_MUTED,
                  }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.86rem",
                    fontWeight: 700,
                    color: showAdvanced ? BLUE_DARK : INK,
                  }}
                >
                  Advanced Settings
                </Typography>
                <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED }}>
                  WhatsApp · Banking · Invoice
                </Typography>
              </Box>
            </Box>

            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 22,
                color: showAdvanced ? BLUE_DARK : INK_MUTED,
                transform: showAdvanced ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </Box>

          {/* Smooth reveal */}
          <Collapse in={showAdvanced} timeout={350}>
            <Box
              sx={{
                border: `1px solid #BFDBFE`,
                borderTop: "none",
                borderRadius: "0 0 12px 12px",
                bgcolor: SURFACE,
                overflow: "hidden",
                // CompanySettings has its own height:100vh — override that here
                "& > div": {
                  height: "auto !important",
                  overflow: "visible !important",
                },
              }}
            >
              <CompanySettings storeID={editStoreData?.store_id} />
            </Box>
          </Collapse>
        </Box>

        {/* SUBMIT — only below lg; wider screens use the header button */}
        <Box
          sx={{
            display: { xs: "flex", lg: "none" },
            alignItems: "center",
            justifyContent: { xs: "stretch", sm: "flex-end" },
            gap: 1.5,
            px: { xs: 1.75, sm: 2.25 },
            py: 1.5,
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: INK_MUTED,
              mr: "auto",
              display: { xs: "none", sm: "block" },
            }}
          >
            Fields marked * are required
          </Typography>

          <SaveButton
            onClick={handleCreateOrUpdateStore}
            loading={loading}
            isEdit={isEdit}
            fullWidth={isMobile}
          />
        </Box>
      </Box>

      {/* Success / Error Dialog */}
      <Dialog
        open={dialogConfig.open}
        onClose={() => setDialogConfig((p) => ({ ...p, open: false }))}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 4,
              width: "90%",
              maxWidth: 340,
              p: 3,
              textAlign: "center",
            },
          },
          backdrop: { sx: { backdropFilter: "blur(4px)" } },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 62,
              height: 62,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: dialogConfig.type === "success" ? "#ECFDF5" : "#FEF2F2",
              border: `1px solid ${
                dialogConfig.type === "success" ? "#A7F3D0" : "#FECACA"
              }`,
              mb: 0.5,
            }}
          >
            {dialogConfig.type === "success" ? (
              <HowToRegRoundedIcon sx={{ fontSize: 32, color: SUCCESS }} />
            ) : (
              <CloseRoundedIcon sx={{ fontSize: 32, color: DANGER }} />
            )}
          </Box>

          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: INK }}>
            {dialogConfig.type === "success"
              ? "Success"
              : "Something went wrong"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: INK_MUTED,
              wordWrap: "break-word",
            }}
          >
            {dialogConfig.message}
          </Typography>

          <Button
            onClick={() => {
              setDialogConfig((p) => ({ ...p, open: false }));
              if (dialogConfig.type === "success")
                setTimeout(() => navigate(-1), 300);
            }}
            sx={{
              mt: 1.5,
              minWidth: 130,
              textTransform: "none",
              fontSize: "0.82rem",
              fontWeight: 700,
              color: "#fff",
              borderRadius: 2,
              py: 0.85,
              bgcolor: dialogConfig.type === "success" ? BLUE_DARK : DANGER,
              "&:hover": {
                bgcolor:
                  dialogConfig.type === "success" ? "#1E3A8A" : "#B91C1C",
              },
            }}
          >
            Done
          </Button>
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ "&.MuiSnackbar-root": { bottom: "56px !important" } }}
      >
        <Alert
          onClose={handleClose}
          severity={snackBarConfig.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontSize: "0.82rem", alignItems: "center" }}
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default StoreMaster;
